import Button from "components/Button/Button";
import Card from "components/Card/Card";
import { KaboodleToaster } from "components/common";
import { Select } from "components/inputs";
import KaboodleModal from "components/Modals/KaboodleModal";
import { useState } from "react";
import {
  useCreatePartABPlanCoverageMutation,
  useCreatePartCDPlanCoverageMutation,
} from "services/part-coverage-api";
import type {
  PartABPlanCoverageTypeEnum,
  PartCDPlanCoverageTypeEnum,
} from "types/DataTypes";
import { useConstants, useCurrentUser } from "utils/hooks";

import type { CreateCoverageData } from "../types";

type AddCoverageModalProps = {
  isOpen: boolean;
  onClose: () => void;
  fileId: number;
  organization: number;
};

export const AddCoverageModal = ({
  isOpen,
  onClose,
  fileId,
  organization,
}: AddCoverageModalProps) => {
  const [selectedType, setSelectedType] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const user = useCurrentUser();
  const { constants } = useConstants();

  const [createPartAB] = useCreatePartABPlanCoverageMutation();
  const [createPartCD] = useCreatePartCDPlanCoverageMutation();

  const availableTypes = [
    ...(constants?.PART_A_B_PLAN_COVERAGE_TYPES?.filter(
      (t: { value: string; label: string }) => t?.value,
    ).map((t: { value: string; label: string }) => ({
      value: t.value,
      label: `Part ${t.value}`,
    })) || []),
    ...(constants?.PART_C_D_PLAN_COVERAGE_TYPES?.filter(
      (t: { value: string; label: string }) => t?.value,
    ).map((t: { value: string; label: string }) => ({
      value: t.value,
      label: `Part ${t.value}`,
    })) || []),
  ];

  const handleSubmit = async () => {
    if (!selectedType) {
      KaboodleToaster.error("Please select a coverage type");
      return;
    }

    setIsSubmitting(true);
    const isABType = ["A", "B"].includes(selectedType);
    const coverageType = isABType
      ? (selectedType as PartABPlanCoverageTypeEnum)
      : (selectedType as PartCDPlanCoverageTypeEnum);

    try {
      const baseData: CreateCoverageData = {
        created_by: user.id,
        organization,
        type: coverageType,
        file: fileId,
      };

      if (isABType) {
        await createPartAB({
          data: baseData,
        }).unwrap();
      } else {
        await createPartCD({
          data: baseData,
        }).unwrap();
      }

      KaboodleToaster.success("Coverage added successfully");
      setSelectedType(null);
      onClose();
    } catch (error) {
      console.error("Failed to create coverage:", error);
      KaboodleToaster.error("Failed to create coverage");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setSelectedType(null);
    onClose();
  };

  return (
    <KaboodleModal
      isOpen={isOpen}
      onClose={handleClose}
      footer={
        <div className="flex jc-c">
          <Button
            onClick={handleSubmit}
            disabled={!selectedType || isSubmitting}
          >
            {isSubmitting ? "Adding..." : "Add Coverage"}
          </Button>
          <Button onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
        </div>
      }
    >
      <Card heading="Add Coverage">
        <div className="form-fields">
          <label htmlFor="coverage-type">Coverage Type</label>
          <Select
            name="coverage-type"
            value={
              selectedType
                ? { value: selectedType, label: `Part ${selectedType}` }
                : null
            }
            onChange={(option: { value: string; label: string } | null) => {
              setSelectedType(option?.value || null);
            }}
            options={availableTypes}
            placeholder="Select Type"
          />
        </div>
      </Card>
    </KaboodleModal>
  );
};
